import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThinkingPostPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/posts/${postId}`);
        setPost(response.data);
      } catch (error) {
        console.error("Error fetching post:", error);
        setError("Post not found");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [postId]);

  if (loading) {
    return (
      <div className="py-32" data-testid="post-loading">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center text-[#607D8B]">
          Loading...
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="py-32" data-testid="post-error">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[#607D8B] mb-8">{error || "Post not found"}</p>
          <button
            onClick={() => navigate("/thinking")}
            className="inline-flex items-center gap-2 text-sm font-bold text-[#1565C0]"
            data-testid="back-to-thinking"
          >
            <ArrowLeft size={16} weight="bold" /> Back to Thinking
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="thinking-post-page">
      {/* Back Link */}
      <section className="pt-12">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link
            to="/thinking"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#607D8B] hover:text-[#1565C0] transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft size={16} weight="bold" /> Back to Thinking
          </Link>
        </div>
      </section>

      {/* Post Content */}
      <motion.article 
        className="py-16 md:py-24"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Header */}
          <header className="mb-12">
            <div className="accent-bar mb-6"></div>
            <h1 
              className="font-['Cabinet_Grotesk',sans-serif] text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-[#0D1B2A] leading-[1.15]"
              data-testid="post-title"
            >
              {post.title}
            </h1>
          </header>

          {/* Content */}
          <div className="space-y-8" data-testid="post-content">
            {post.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-lg md:text-xl text-[#3D5A73] leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Footer */}
          <footer className="mt-16 pt-12 border-t-2 border-[#E0E7ED]">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 text-[#1565C0] font-bold hover:text-[#0D47A1] transition-all"
              data-testid="post-cta"
            >
              Have questions? Let's talk <ArrowRight size={18} weight="bold" className="arrow-animate" />
            </Link>
          </footer>
        </div>
      </motion.article>
    </div>
  );
}
